import React, { useState, useEffect, useRef } from 'react';
import { VictoryPie, VictoryLabel } from 'victory';
import VictoryPieCustomTooltip from './VictoryPieCustomTooltip';
import { emotionColors, emotionOrder } from '../utils';

const PIE_TOOLTIP_WIDTH = 300;
const PIE_TOOLTIP_HALF_WIDTH = PIE_TOOLTIP_WIDTH / 2;
const PIE_TOOLTIP_OFFSET = 28;
const PIE_TOOLTIP_EDGE_PADDING = 12;

const PieGraph = ({ pieGraphData, tooltipCounts = {}, chosenTime, chosenTimeCompare, studentEmailToNameMap, chosenStudent }) => {
  const [activeEmotion, setActiveEmotion] = useState(null);
  const [tooltipEmotion, setTooltipEmotion] = useState(null);
  const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0 });
  const isEmptyState = pieGraphData.every(d => d.current === 0)

  const total = pieGraphData.reduce((sum, { current }) => sum + current, 0);

  const pieData = emotionOrder
  .map((name, index) => {
    const match = pieGraphData.find(d => d.name === name);
    if (!match) return null;
    const percentage = total > 0 ? Number(((match.current / total) * 100).toFixed(1)) : 0;
    const color = emotionColors[name] || '#ccc';
    return {
      x: name,
      y: total > 0 ? match.current : 1,
      index,
      percentage,
      color
    };
  })
    .filter(Boolean);

  useEffect(() => {
    if (pieData.length > 0 && !activeEmotion) {
      let maxPercentage = -Infinity;
      let dominantEmotion = null;
      
      pieData.forEach(datum => {
        if (datum.percentage > maxPercentage) {
          maxPercentage = datum.percentage;
          dominantEmotion = datum.x;
        }
      });

      if (maxPercentage > 0) {
        setActiveEmotion(dominantEmotion);
      }
    }
  }, [pieData, activeEmotion]);

  const handleSliceClick = (evt, { datum }) => {
    const container = evt.currentTarget.closest('.pie-container');
    const containerRect = container?.getBoundingClientRect();

    if (!containerRect) return;

    const sliceRect = evt.target.getBoundingClientRect();
    const sliceCenterX = sliceRect.left + sliceRect.width / 2 - containerRect.left;
    const sliceCenterY = sliceRect.top + sliceRect.height / 2 - containerRect.top;
    const widgetMidpointX = containerRect.width / 2;
    const shouldShowOnRight = sliceCenterX >= widgetMidpointX;
    let localX;
    if (shouldShowOnRight) {
      localX = sliceCenterX + PIE_TOOLTIP_HALF_WIDTH + PIE_TOOLTIP_OFFSET;
    } else {
      localX = sliceCenterX - PIE_TOOLTIP_HALF_WIDTH - PIE_TOOLTIP_OFFSET;
    }

    localX = Math.min(
      containerRect.width - PIE_TOOLTIP_HALF_WIDTH - PIE_TOOLTIP_EDGE_PADDING,
      Math.max(PIE_TOOLTIP_HALF_WIDTH + PIE_TOOLTIP_EDGE_PADDING, localX),
    );

    const wasActive = tooltipEmotion === datum.x;

    setActiveEmotion(datum.x);
    setTooltipEmotion(wasActive ? null : datum.x);

    setTooltipPosition({ x: localX, y: sliceCenterY });
  };

  const pieRef = useRef(null); 

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (pieRef.current && !pieRef.current.contains(e.target)) {
        setTooltipEmotion(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  return (
    <div className="w-auto h-[400px] 2xl:h-[500px] m-auto relative pie-container">
      <VictoryPie
        data={pieData}
        innerRadius={({ datum }) => activeEmotion === datum.x ? 80 : 100}
        radius={120}
        padAngle={2} 
        startAngle={-60}
        endAngle={300}
        labels={({ datum }) => {
          if (!isEmptyState && datum.percentage === 0) return '';
          return `${datum.x} ${datum.percentage}%`;
        }}
        
        labelPosition={"centroid"}
        labelPlacement={"vertical"}
  
        labelRadius={({ datum }) => {
          const baseRadius = 160;
          const angle = (datum.startAngle + datum.endAngle) / 2;
          if (isEmptyState) {
            if ((angle >= -60 && angle <= -20) || (angle >= 20 && angle <= 60) ) {
              return baseRadius + 20;
            } else if (angle >= 140 && angle <= 220) {
              return baseRadius + 10;
            }
            return baseRadius;
          }
          if (angle >= 200 && angle <= 240) {
            return baseRadius - 5;
          } else if (angle >= 240 && angle <= 300) {
            return baseRadius - 20;
          } else if (angle >= -60 && angle < -45) {
            return baseRadius + 20;
          } else if (angle >= -45 && angle < 0) {
            return baseRadius + 10;
          } else if (angle >= 0 && angle < 30) {
            return baseRadius - 12;
          } else if (angle >= 50 && angle < 120) {
            return baseRadius - 22;
          }
          return baseRadius;
        }}

        labelComponent={
          <VictoryLabel
            verticalAnchor="middle"
            dy={0}
            style={[
              {
                fill: ({ datum }) => emotionColors[datum.x] || '#cccccc',
                fontSize: 14,
                fontWeight: 400,
                textTransform: 'capitalize'
              }
            ]}
          />
        }
        style={{
          data: {
            fill: ({ datum }) => datum.color
          }
        }}
        events={[
          {
            target: 'data',
            eventHandlers: {
              onClick: handleSliceClick
            }
          }
        ]}
      />
      {tooltipEmotion && (
        <div ref={pieRef}>
         <VictoryPieCustomTooltip
            datum={{
              x: tooltipEmotion,
              y: pieGraphData.find(d => d.name === tooltipEmotion)?.current || 0,
              compare: pieGraphData.find(d => d.name === tooltipEmotion)?.compare || 0
            }}
            x={tooltipPosition.x}
            y={tooltipPosition.y}
            tooltipCounts={tooltipCounts}
            state={{
              counts: pieGraphData,
              tooltipCounts,
              chosenTime,
              chosenTimeCompare,
              studentEmailToNameMap: studentEmailToNameMap
            }}
          />
        </div>
      )}

      {activeEmotion ? (
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 text-center pointer-events-none flex flex-col items-center justify-center">
         {chosenStudent && chosenStudent !== 'All Students'  && chosenStudent !== 'Choose Student' ? (
            <div className="text-[17px] text-400 font-medium max-w-[150px] flex-wrap">
              {studentEmailToNameMap?.[chosenStudent] || chosenStudent}
            </div>
          ) : (
            <div className="text-[17px] text-400 font-medium">
              {Object.keys(tooltipCounts?.[activeEmotion] || {}).length}{' '}
              {Object.keys(tooltipCounts?.[activeEmotion] || {}).length === 1 ? 'Student' : 'Students'}
            </div>
          )}
          <div className="w-[80%] mx-auto my-1.5 border-t border-gray-400" />
          <div className="capitalize text-[24px] font-bold text-700">
            {activeEmotion}
          </div>
        </div>
      ) : (
        total === 0 && (
          <div className="text-4xl text-400 absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 text-center pointer-events-none flex flex-col items-center justify-center">0 Students</div>
      )
      )}
    </div>
  );
};

export default PieGraph;